// API Service for making authenticated requests
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem("authToken");
  }

  // Get auth headers
  getAuthHeaders(isFormData = false) {
    const token = this.getAuthToken();
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    // Don't set Content-Type for FormData - browser will set it automatically with boundary
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Check if body is FormData
    const isFormData = options.body instanceof FormData;

    const config = {
      headers: this.getAuthHeaders(isFormData),
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle successful responses with no content (like DELETE operations)
      if (response.status === 204 || response.status === 205) {
        return null; // No content to parse
      }

      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        
        // If it's an empty response and the status is successful, that's OK
        if (!textResponse && response.ok) {
          return null;
        }
        
        console.error("Non-JSON response received:", textResponse);

        // Check if it's an HTML error page (like 404 or 500)
        if (
          textResponse.includes("<!DOCTYPE") ||
          textResponse.includes("<html")
        ) {
          throw new Error(
            "Server error: Unable to connect to the backend service. Please check if the server is running.",
          );
        }

        throw new Error(
          `Expected JSON response but got: ${contentType}. Response: ${textResponse.substring(0, 200)}...`,
        );
      }

      const data = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          // Token expired or invalid
          localStorage.removeItem("authToken");
          localStorage.removeItem("userInfo");

          // Don't redirect if we're already on login page or during login process
          if (
            !window.location.pathname.includes("/login") &&
            !endpoint.includes("/auth/")
          ) {
            window.location.href = "/login";
          }

          throw new Error(data.msg || "Authentication failed");
        }

        // Handle specific error messages from backend
        const errorMessage =
          data.msg || data.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error("API request failed:", error);

      // Handle network errors
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          "Network error: Unable to connect to the server. Please check your internet connection.",
        );
      }

      throw error;
    }
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }

  // POST request
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint, data = null) {
    const options = { method: "DELETE" };
    if (data) {
      options.body = JSON.stringify(data);
    }
    return this.request(endpoint, options);
  }

  // Authentication specific methods
  async sendOTP(email) {
    return this.request("/auth/check-email-exists", {
      method: "POST",
      body: JSON.stringify({ username: email }),
    });
  }

  async verifyOTP(email, otp) {
    return this.request("/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify({ username: email, password: otp }),
    });
  }

  async loginWithClient(email, selectedClient) {
    return this.request("/auth/login-with-client", {
      method: "POST",
      body: JSON.stringify({
        username: email,
        selected_client: selectedClient,
      }),
    });
  }

  // Legacy login method (kept for backward compatibility)
  async login(email, otp, clientId = null) {
    const payload = { username: email, password: otp };
    if (clientId) {
      payload.client_id = clientId;
    }

    return this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Application specific methods
  async getApplications() {
    return this.get("/applications");
  }

  async getClients() {
    return this.get("/clients");
  }

  async getDashboard() {
    return this.get("/dashboard");
  }

  async getJobs() {
    return this.get("/jobs");
  }

  async getEnvironments() {
    return this.get("/environments");
  }
  async getLogicalGroups() {
    return this.get("/logical-groups");
  }

  // Add more API methods as needed for your application
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
